import { useQuery } from '@tanstack/react-query';
import type { PriceHistoryDTO, ProductDTO } from '@bifeai/shared';
import { Button, Callout, Dialog, EmptyState, Field, Input, Skeleton } from '@bifeai/ui';
import { History } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useCatalogAccess } from '../../lib/catalog';
import { formatDateTime, formatMoney } from '../../lib/format';
import { centsToPesosInput, pesosToCents, priceUnit } from '../../lib/units';

/** Cambia el precio general o el de una sucursal. Todo cambio queda en el historial. */
export function PriceDialog({ product, onClose, onSaved }: { product: ProductDTO; onClose: () => void; onSaved: (p: ProductDTO) => Promise<void> }) {
  const access = useCatalogAccess();
  const branchOverride = access.priceBranchId ? (product.branchPrices.find((b) => b.branchId === access.priceBranchId)?.priceCents ?? null) : null;
  // La central decide si cambia el general o solo el de la sucursal elegida; una sucursal, solo el suyo.
  const [target, setTarget] = useState<'general' | 'branch'>(
    !access.canGeneralPrice || (access.priceBranchId && branchOverride !== null) ? 'branch' : 'general',
  );
  const startCents = target === 'branch' ? (branchOverride ?? product.generalPriceCents) : product.generalPriceCents;
  const [value, setValue] = useState(centsToPesosInput(startCents));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const cents = pesosToCents(value);
  const base = target === 'branch' ? (branchOverride ?? product.generalPriceCents) : product.generalPriceCents;
  const change = cents !== null && base ? ((cents - base) / base) * 100 : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (cents === null) return setError('Ingresá un importe válido.');
    setSaving(true);
    setError(null);
    try {
      const saved = await api.put<ProductDTO>(`/products/${product.id}/price`, {
        priceCents: cents,
        branchId: target === 'branch' ? access.priceBranchId : null,
      });
      await onSaved(saved);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function resetBranch() {
    if (!access.priceBranchId) return;
    setSaving(true);
    try {
      await api.del(`/products/${product.id}/price/${access.priceBranchId}`);
      const fresh = await api.get<ProductDTO>(`/products/${product.id}`);
      await onSaved(fresh);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()} title="Cambiar precio" description={product.name}>
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}

        {access.canGeneralPrice && access.priceBranchId && (
          <div className="bf-radio-list" role="radiogroup" aria-label="A qué precio aplica">
            <label className="bf-radio">
              <input
                type="radio"
                name="target"
                checked={target === 'general'}
                onChange={() => {
                  setTarget('general');
                  setValue(centsToPesosInput(product.generalPriceCents));
                }}
              />
              <span>
                Precio general
                <small>Aplica a todas las sucursales que no tengan precio propio.</small>
              </span>
            </label>
            <label className="bf-radio">
              <input
                type="radio"
                name="target"
                checked={target === 'branch'}
                onChange={() => {
                  setTarget('branch');
                  setValue(centsToPesosInput(branchOverride ?? product.generalPriceCents));
                }}
              />
              <span>
                Solo en {access.priceBranchName}
                <small>{branchOverride !== null ? 'Hoy tiene precio propio.' : 'Hoy usa el precio general.'}</small>
              </span>
            </label>
          </div>
        )}
        {!access.canGeneralPrice && (
          <Callout>
            Cambiás el precio de <strong>{access.priceBranchName}</strong>. El precio general lo define la casa central.
          </Callout>
        )}

        <Field
          label={`Nuevo precio (${priceUnit(product.unitType)})`}
          hint={
            change !== null && Number.isFinite(change) && Math.abs(change) >= 0.05
              ? `${change > 0 ? 'Sube' : 'Baja'} ${Math.abs(change).toLocaleString('es-AR', { maximumFractionDigits: 1 })}% respecto de ${formatMoney(base!)}`
              : base
                ? `Precio actual: ${formatMoney(base)}`
                : 'Todavía no tiene precio.'
          }
        >
          <Input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} autoFocus placeholder="$ 0" />
        </Field>

        <div className="bf-form-actions">
          {target === 'branch' && branchOverride !== null && access.priceBranchId && (
            <Button variant="ghost" onClick={() => void resetBranch()} disabled={saving} style={{ marginRight: 'auto' }}>
              Volver al precio general
            </Button>
          )}
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={saving}>
            Guardar precio
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function PriceHistoryDialog({ product, onClose }: { product: ProductDTO; onClose: () => void }) {
  const { branchName } = useCatalogAccess();
  const history = useQuery({
    queryKey: ['price-history', product.id],
    queryFn: () => api.get<PriceHistoryDTO[]>(`/products/${product.id}/price-history`),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Historial de precios" description={product.name} wide>
      {history.isPending ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <Skeleton />
          <Skeleton />
        </div>
      ) : history.data?.length === 0 ? (
        <EmptyState icon={<History />} title="Sin cambios de precio" />
      ) : (
        <div className="bf-scroll-box">
          <table className="bf-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Aplica a</th>
                <th>Antes</th>
                <th>Después</th>
                <th>Motivo</th>
                <th>Quién</th>
              </tr>
            </thead>
            <tbody>
              {history.data?.map((h) => {
                const up = h.oldPriceCents !== null && h.newPriceCents > h.oldPriceCents;
                return (
                  <tr key={h.id}>
                    <td className="bf-cell-sub">{formatDateTime(h.createdAt)}</td>
                    <td>{h.branchId ? (branchName(h.branchId) ?? 'Sucursal') : 'General'}</td>
                    <td className="bf-num bf-text-muted">{h.oldPriceCents !== null ? formatMoney(h.oldPriceCents) : '—'}</td>
                    <td className={`bf-num ${up ? 'bf-text-warning' : ''}`}>{formatMoney(h.newPriceCents)}</td>
                    <td>{h.reason ?? '—'}</td>
                    <td className="bf-cell-sub">{h.changedByName ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}
